/**
 * Client-side locale-aware product sorting
 * Sorts products alphabetically based on displayed product titles in current language
 * Only active for French locale
 */

class LocaleProductSort {
  constructor(container) {
    this.container = container;
    this.productsGrid = container.querySelector('.js-grid');
    this.sortLinks = container.querySelectorAll('[data-sort-link]');
    this.currentLocale = document.documentElement.lang || 'en';
    
    if (this.productsGrid && this.sortLinks.length > 0) {
      this.init();
    }
  }

  init() {
    // Listen for the theme:form:filter event that the existing theme triggers
    this.container.addEventListener('theme:form:filter', (e) => {
      const sortValue = e.detail?.params;
      
      if (sortValue === 'title-ascending' || sortValue === 'title-descending') {
        // Prevent the AJAX request
        e.stopImmediatePropagation();
        e.preventDefault();
        
        const direction = sortValue === 'title-ascending' ? 'asc' : 'desc';
        
        // Perform the sort
        this.sortProducts(direction);
        
        // Update URL without reload
        this.updateURL(sortValue);
      }
    }, true);
    
    // Also check on page load if we have a sort parameter
    this.checkInitialSort();
  }

  checkInitialSort() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    
    if (sortBy === 'title-ascending' || sortBy === 'title-descending') {
      const direction = sortBy === 'title-ascending' ? 'asc' : 'desc';
      
      // Apply sort after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.sortProducts(direction);
      }, 200);
    }
  }

  sortProducts(direction) {
    // Get all list items
    const allItems = Array.from(this.productsGrid.querySelectorAll('li'));
    
    // Separate products from non-products (promo cards)
    const productItems = [];
    const nonProductItems = [];
    
    allItems.forEach((item, index) => {
      const hasProductTitle = item.querySelector('[data-product-title]');
      const isPromoCard = item.querySelector('.promo-card');
      
      if (hasProductTitle && !isPromoCard) {
        productItems.push({
          element: item,
          title: this.getProductTitle(item),
          originalIndex: index
        });
      } else {
        nonProductItems.push({
          element: item,
          originalIndex: index
        });
      }
    });

    // Sort products based on displayed title
    productItems.sort((a, b) => {
      const comparison = a.title.localeCompare(b.title, this.currentLocale, {
        sensitivity: 'base',
        ignorePunctuation: true
      });
      
      return direction === 'asc' ? comparison : -comparison;
    });

    // Clear the grid
    this.productsGrid.innerHTML = '';

    // Rebuild the grid maintaining promo card positions
    let productIndex = 0;
    const maxIndex = Math.max(...allItems.map((_, i) => i));
    
    for (let i = 0; i <= maxIndex; i++) {
      // Check if there's a non-product at this position
      const nonProduct = nonProductItems.find(np => np.originalIndex === i);
      
      if (nonProduct) {
        this.productsGrid.appendChild(nonProduct.element);
      } else if (productIndex < productItems.length) {
        this.productsGrid.appendChild(productItems[productIndex].element);
        productIndex++;
      }
    }

    // Append any remaining products
    while (productIndex < productItems.length) {
      this.productsGrid.appendChild(productItems[productIndex].element);
      productIndex++;
    }
  }

  getProductTitle(productItem) {
    // First try to get from data attribute (most reliable)
    const cardWithData = productItem.querySelector('[data-product-title]');
    if (cardWithData) {
      const title = cardWithData.getAttribute('data-product-title');
      if (title) {
        return title.trim();
      }
    }

    // Fallback: try to find the title element
    const titleElement = productItem.querySelector('.tz-product-card-brand');
    if (titleElement && titleElement.textContent) {
      return titleElement.textContent.trim();
    }

    return '';
  }

  updateURL(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    window.history.pushState({ sort: sortValue }, '', url.toString());
  }
}

// Global instance tracker
let localeSort = null;

// Initialize function - only for French locale
function initLocaleSort() {
  // Check if we're on a French page
  const isFrench = window.location.pathname.includes('/fr');
  
  if (!isFrench) {
    return; // Exit if not French
  }
  
  const collectionSection = document.querySelector('[data-section-type="collection"]');
  if (collectionSection) {
    localeSort = new LocaleProductSort(collectionSection);
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initLocaleSort);

// Re-initialize after AJAX updates (pagination, filters)
document.addEventListener('DOMContentLoaded', () => {
  const collectionSection = document.querySelector('[data-section-type="collection"]');
  if (collectionSection) {
    const productsContainer = collectionSection.querySelector('[data-products-grid]');
    if (productsContainer) {
      // Watch for content changes (pagination, filters)
      const observer = new MutationObserver((mutations) => {
        // Check if the grid content actually changed
        const gridChanged = mutations.some(mutation => 
          mutation.type === 'childList' && 
          mutation.target.classList.contains('collection__products')
        );
        
        if (gridChanged) {
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            initLocaleSort();
            // The checkInitialSort() in the constructor will automatically
            // apply the sort if sort_by is in the URL
          }, 200);
        }
      });
      
      observer.observe(productsContainer, {
        childList: true,
        subtree: true
      });
    }
  }
});
